import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Shield, Users, Store, Package, Tag, ScrollText, ArrowLeft, Upload } from "lucide-react";
import client, { money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import AdminOverview from "@/components/AdminOverview";
import AdminInsights from "@/components/AdminInsights";

export default function Admin() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState("overview");
  const [rows, setRows] = useState([]);
  const [csv, setCsv] = useState("");
  const [importResult, setImportResult] = useState(null);

  useEffect(() => { client.get("/admin/stats").then((r) => setStats(r.data)).catch(() => toast.error("Admin only")); }, []);

  const loadTab = async (t) => {
    setTab(t);
    const map = { products: "products", prices: "prices", promotions: "promotions", stores: "store_locations", users: "users", logs: "audit-logs" };
    if (map[t]) {
      const { data } = await client.get(`/admin/${map[t]}`); setRows(data.slice(0, 60));
    }
  };

  const runImport = async (commit) => {
    const lines = csv.trim().split("\n").filter(Boolean);
    const rowsParsed = lines.map((l) => {
      const [name, brand, category, package_qty, package_unit, price, store_code] = l.split(",").map((x) => x.trim());
      return { name, brand, category, package_qty: +package_qty || 1, package_unit, price, store_code };
    });
    const { data } = await client.post("/admin/import/products", { rows: rowsParsed, commit });
    setImportResult(data);
    if (commit && data.committed) toast.success(`Imported ${data.committed} products`);
  };

  if (!stats) return <div className="grid place-items-center py-20"><Shield className="h-8 w-8 text-pp-forest animate-pulse" /></div>;

  const STAT = [["Users", stats.users, Users], ["Retailers", stats.retailers, Store], ["Stores", stats.stores, Store],
    ["Products", stats.products, Package], ["Prices", stats.prices, Tag], ["Promotions", stats.promotions, Tag],
    ["Trips", stats.trips, ScrollText], ["Lists", stats.lists, ScrollText]];

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate("/")} className="rounded-full bg-white p-2 pp-shadow"><ArrowLeft className="h-5 w-5" /></button>
        <div><h1 className="text-3xl font-extrabold text-pp-text flex items-center gap-2"><Shield className="h-7 w-7 text-pp-forest" /> Admin Dashboard</h1>
          <p className="text-pp-muted text-sm">Growth, savings, pricing freshness and catalogue tools.</p></div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {STAT.map(([label, val, Icon]) => (
          <div key={label} className="bg-white rounded-2xl p-4 pp-shadow" data-testid={`stat-${label.toLowerCase()}`}>
            <Icon className="h-5 w-5 text-pp-forest" /><p className="mt-2 text-2xl font-extrabold text-pp-text">{val}</p><p className="text-xs text-pp-muted">{label}</p>
          </div>
        ))}
      </div>

      <Tabs value={tab} onValueChange={loadTab}>
        <TabsList className="bg-white rounded-full p-1 flex-wrap h-auto">
          {["overview", "shoppers", "products", "prices", "promotions", "stores", "users", "logs", "import"].map((t) => (
            <TabsTrigger key={t} value={t} data-testid={`admin-tab-${t}`} className="rounded-full capitalize data-[state=active]:bg-pp-forest data-[state=active]:text-white">{t}</TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="overview"><AdminOverview /></TabsContent>
        <TabsContent value="shoppers"><AdminInsights /></TabsContent>

        {["products", "prices", "promotions", "stores", "users", "logs"].map((t) => (
          <TabsContent key={t} value={t}>
            <div className="bg-white rounded-3xl p-4 pp-shadow overflow-x-auto">
              <Table>
                <TableHeader><TableRow>{rows[0] && Object.keys(rows[0]).slice(0, 6).map((k) => <TableHead key={k}>{k}</TableHead>)}</TableRow></TableHeader>
                <TableBody>
                  {rows.map((r, i) => (
                    <TableRow key={i} data-testid={`admin-row-${t}-${i}`}>
                      {Object.keys(rows[0]).slice(0, 6).map((k) => (
                        <TableCell key={k} className="text-xs max-w-[180px] truncate">{typeof r[k] === "object" ? JSON.stringify(r[k]) : String(r[k] ?? "—")}</TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {rows.length === 0 && <p className="text-center text-pp-muted py-8">No records.</p>}
            </div>
          </TabsContent>
        ))}

        <TabsContent value="import">
          <div className="bg-white rounded-3xl p-5 pp-shadow space-y-3">
            <h2 className="font-extrabold text-pp-text flex items-center gap-2"><Upload className="h-5 w-5 text-pp-forest" /> CSV product import</h2>
            <p className="text-sm text-pp-muted">Format: <code>name,brand,category,package_qty,package_unit,price,store_code</code>. Validate before committing.</p>
            <textarea data-testid="csv-input" value={csv} onChange={(e) => setCsv(e.target.value)} rows={6}
              className="w-full rounded-2xl border border-pp-border p-3 text-sm font-mono bg-pp-cream"
              placeholder={"Almond Milk,Silk,Dairy & Eggs,1.89,L,4.49,walmart"} />
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => runImport(false)} data-testid="validate-import" className="rounded-full bg-white">Validate</Button>
              <Button onClick={() => runImport(true)} data-testid="commit-import" className="rounded-full bg-pp-forest">Commit import</Button>
            </div>
            {importResult && (
              <div className={`rounded-2xl p-3 text-sm ${importResult.errors.length ? "bg-[#FDF0ED]" : "bg-[#EBF7EE]"}`} data-testid="import-result">
                <p className="font-bold">{importResult.valid} valid · {importResult.errors.length} errors · {importResult.committed} committed</p>
                {importResult.errors.map((e, i) => <p key={i} className="text-pp-error text-xs">Row {e.row}: {e.error}</p>)}
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
